import { useState } from 'react';
import axios from 'axios';
import { QueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import useGetMsalAccessToken from './useGetMsalAccessToken';

export const ACTIVE_DIRECTORY_USER_THUMBNAIL_QUERY_KEY = 'active-directory-user-thumbnail';
export const ACTIVE_DIRECTORY_USER_THUMBNAIL_CACHE_KEY = 'ap-calendar-thumbnail-cache';
const ONE_DAY = 1000 * 60 * 60 * 24;

const thumbnailQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // TanStack Query falls back to a 5 minute GC window during hydration unless this is set here.
      gcTime: ONE_DAY,
    },
  },
});

// Async wrapper around localStorage so TanStack can await read/write operations.
const thumbnailPersister = createAsyncStoragePersister({
  storage: {
    getItem: async (key) => window.localStorage.getItem(key),
    setItem: async (key, value) => window.localStorage.setItem(key, value),
    removeItem: async (key) => window.localStorage.removeItem(key),
  },
  key: ACTIVE_DIRECTORY_USER_THUMBNAIL_CACHE_KEY,
});

// Wire the dedicated query client to localStorage so thumbnails survive page reloads.
// - maxAge: cached entries older than 1 day are discarded on restore, forcing a refetch.
// - shouldDehydrateQuery: only thumbnail queries are written to storage; other queries
//   on this client (if any) are never persisted.
persistQueryClient({
  queryClient: thumbnailQueryClient,
  persister: thumbnailPersister,
  maxAge: ONE_DAY,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => query.queryKey[0] === ACTIVE_DIRECTORY_USER_THUMBNAIL_QUERY_KEY,
  },
});

const blobToDataUrl = async (blob: Blob): Promise<string> => {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }

      reject(new Error('Unable to convert blob to data URL'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read blob'));

    reader.readAsDataURL(blob);
  });
};

/**
 * Get the profile thumbnail image for a given user.
 *
 * Caching strategy (all three timers are aligned to 1 day):
 *  - staleTime (1d): the cached data URL is considered fresh; no refetch is triggered.
 *  - gcTime   (1d): once a query has no observers, it is kept in memory for 1 day
 *                   before being garbage-collected.
 *  - maxAge   (1d): when the app starts, persisted entries older than 1 day are
 *                   dropped from localStorage and the thumbnail is fetched again.
 */
function useGetActiveDirectoryUserThumbnail() {
  const [loading, setLoading] = useState(false);
  const { getAccessToken } = useGetMsalAccessToken();

  /**
   * Get an Active Directory user's image as a base64 data URL.
   * Returns the in-memory or localStorage-persisted value when still fresh (< 1d),
   * otherwise fetches from the API and stores the result.
   *
   * @param {string} userId The user's ID
   * @return {string|undefined}
   */
  const getUserThumbnail = async (userId: string): Promise<Blob | string | undefined> => {
    setLoading(true);

    try {
      return await thumbnailQueryClient.fetchQuery<string | undefined>({
        queryKey: [ACTIVE_DIRECTORY_USER_THUMBNAIL_QUERY_KEY, userId],
        staleTime: ONE_DAY,
        gcTime: ONE_DAY,
        queryFn: async () => {
          const accessToken = await getAccessToken();
          const headers = {
            Authorization: `Bearer ${accessToken}`,
          };

          const url = `${import.meta.env.VITE_API_URL}/users/${userId}/thumbnail`;
          const result = await axios.get(url, { headers, responseType: 'blob' });

          if (result.status !== 200) {
            return undefined;
          }

          return await blobToDataUrl(result.data);
        },
      });
    } catch (err) {
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  return { getUserThumbnail, loading };
}

export default useGetActiveDirectoryUserThumbnail;
